import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useLanguages } from '../../hooks/useLanguages';
import { loadVocabularyCatalog, loadConsolidatedVocabularyTopic } from '../../services/vocabularyV2';
import { loadGrammarManifest, loadGrammarSections } from '../../services/grammarV2';
import { StreakService } from '../../services/StreakService';
import { XpService } from '../../services/XpService';
import { SoundEffectService } from '../../services/SoundEffectService';
import { LearningProgressService } from '../../services/LearningProgressService';
import { GameProgressBar, Confetti, StarRow, ShakeWidget, FloatingXpBadge, SkeletonGrid } from '../../components/GameUI';

// Milestone bonus battle mixing three question styles — vocabulary,
// listening, and grammar (recognizing which grammar chapter a sentence
// belongs to, since per-sentence native translations aren't available in
// the grammar content). Port of the mobile app's
// lib/games/boss_battle/boss_battle_screen.dart. Unlike the other ported
// games, this one is fully self-contained: it fetches its own vocabulary
// topic + grammar chapters on mount (mirroring the Dart source's
// `BossBattleScreen.start()` static method) instead of receiving a word
// list via router state from a picker screen.

const PHASE_EMOJI = { vocabulary: '👹', listening: '👺', grammar: '🐉' };

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function textOf(entry) {
  if (entry && typeof entry === 'object') return entry.text == null ? '' : String(entry.text);
  if (typeof entry === 'string') return entry;
  return '';
}

function BossOptionTile({ text, revealed, isAnswer, isPicked, onClick }) {
  let bg = '#FFFFFF';
  let border = '#E2E8F0';
  let fg = '#0F172A';
  if (revealed) {
    if (isAnswer) {
      bg = '#D7FFB8'; border = '#58CC02'; fg = '#2B6B00';
    } else if (isPicked) {
      bg = '#FFDFE0'; border = '#FF4B4B'; fg = '#8B0000';
    } else {
      bg = '#FFFFFF'; border = '#F1F5F9'; fg = '#CBD5E1';
    }
  }
  return (
    <button
      type="button"
      disabled={revealed}
      onClick={onClick}
      style={{ background: bg, borderColor: border, color: fg }}
      className="w-full rounded-2xl border-[2.5px] px-4 py-3.5 text-center text-sm font-extrabold leading-snug transition-colors duration-150"
    >
      {text}
    </button>
  );
}

export default function BossBattleGame() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { languagePrefs } = useAuth();
  const { languages, loading: languagesLoading } = useLanguages();

  const nativeCode = languagePrefs?.nativeLanguage || '';
  const learnCode = languagePrefs?.targetLanguage || '';
  const langPair = `${nativeCode}_${learnCode}`;

  const langMap = useMemo(() => Object.fromEntries(languages.map((l) => [l.code, l])), [languages]);
  const nativeLang = langMap[nativeCode] || { code: nativeCode, label: nativeCode, dir: 'ltr', tts: nativeCode };
  const targetLang = langMap[learnCode] || { code: learnCode, label: learnCode, dir: 'ltr', tts: learnCode };

  // 'loading' | 'insufficient' | 'ready'
  const [loadState, setLoadState] = useState('loading');
  const [questions, setQuestions] = useState([]);

  const [index, setIndex] = useState(0);
  const [hp, setHp] = useState(0);
  const [lives, setLives] = useState(3);
  const [correctCount, setCorrectCount] = useState(0);
  const [picked, setPicked] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [hitKey, setHitKey] = useState(0);
  const [showIntro, setShowIntro] = useState(true);
  const [done, setDone] = useState(false);
  const [won, setWon] = useState(false);

  const doneRef = useRef(false);
  const rewardedRef = useRef(false);

  // Redirect immediately if there is no language pair to work with — same
  // "guard against insufficient data" convention every other ported game
  // follows, just checked against auth state instead of router state.
  useEffect(() => {
    if (!nativeCode || !learnCode) navigate('/games', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch a random thematic vocabulary topic (for the vocabulary + listening
  // phases) and a sample of grammar chapters (for the grammar phase), then
  // assemble the full question pool — mirrors the Dart source's
  // `BossBattleScreen.start()` exactly, including its "fail quietly, just
  // end up with fewer questions" error handling per phase.
  useEffect(() => {
    if (!nativeCode || !learnCode || languagesLoading) return undefined;
    let active = true;
    setLoadState('loading');

    const targetDir = targetLang.dir || 'ltr';
    const nativeDir = nativeLang.dir || 'ltr';

    (async () => {
      let pairs = [];
      try {
        const catalog = await loadVocabularyCatalog(learnCode);
        const topics = catalog?.thematic || [];
        if (topics.length) {
          const topicId = topics[Math.floor(Math.random() * topics.length)]?.id;
          if (topicId) {
            const pack = await loadConsolidatedVocabularyTopic('thematic', learnCode, nativeCode, learnCode, topicId);
            const nativeContent = pack.reader?.content || {};
            const targetContent = pack.target?.content || {};
            Object.keys(targetContent).forEach((key) => {
              const n = textOf(nativeContent[key]);
              const tg = textOf(targetContent[key]);
              if (n && tg) pairs.push({ id: key, native: n, target: tg });
            });
          }
        }
      } catch {
        // No vocabulary data for this language pair — vocab/listening phases
        // just end up empty, same as the Dart source's silent try/catch.
      }

      function wordQuestions(count, audio) {
        if (pairs.length < 4) return [];
        return shuffle(pairs).slice(0, count).map((answer) => {
          const distractors = shuffle(pairs.filter((p) => p.id !== answer.id)).slice(0, 3);
          const options = shuffle([answer.native, ...distractors.map((p) => p.native)]);
          return {
            phase: audio ? 'listening' : 'vocabulary',
            prompt: answer.target,
            promptDir: targetDir,
            options,
            correctOption: answer.native,
            optionsDir: nativeDir,
            speakPrompt: audio,
          };
        });
      }

      const vocabQuestions = wordQuestions(4, false);
      const listeningQuestions = wordQuestions(4, true);

      const grammarQuestions = [];
      try {
        const chapters = await loadGrammarManifest(learnCode);
        const sectionsDoc = await loadGrammarSections(learnCode);
        const sectionById = {};
        (sectionsDoc?.sections || []).forEach((raw) => {
          if (raw && raw.id != null) sectionById[String(raw.id)] = raw;
        });
        const allLabels = chapters
          .map((c) => sectionById[c.id]?.label || '')
          .filter((l) => l);
        if (chapters.length >= 4) {
          const sampled = shuffle(chapters).slice(0, 6);
          for (const chapter of sampled) {
            if (grammarQuestions.length >= 4) break;
            const section = sectionById[chapter.id];
            if (!section) continue;
            const examples = (section.examples || []).filter((e) => e && typeof e === 'object');
            if (!examples.length) continue;
            const example = examples[Math.floor(Math.random() * examples.length)];
            const text = (example.text || '').toString().trim();
            const correctLabel = section.label || '';
            if (!text || !correctLabel) continue;
            const distractors = shuffle(Array.from(new Set(allLabels.filter((l) => l !== correctLabel))));
            if (distractors.length < 3) continue;
            const options = shuffle([correctLabel, ...distractors.slice(0, 3)]);
            grammarQuestions.push({
              phase: 'grammar',
              prompt: text,
              promptDir: targetDir,
              options,
              correctOption: correctLabel,
              optionsDir: nativeDir,
              speakPrompt: false,
            });
          }
        }
      } catch {
        // No grammar data for this language pair — grammar phase just ends
        // up empty, same as the Dart source's silent try/catch.
      }

      const allQuestions = [...vocabQuestions, ...listeningQuestions, ...grammarQuestions];
      if (!active) return;
      if (allQuestions.length < 6) {
        setLoadState('insufficient');
        return;
      }
      setQuestions(allQuestions);
      setLoadState('ready');
    })();

    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nativeCode, learnCode, languagesLoading]);

  function maybeSpeak(question) {
    if (!question?.speakPrompt) return;
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(question.prompt);
    utterance.lang = targetLang.tts || learnCode;
    window.speechSynthesis.speak(utterance);
  }

  // Initialize the battle (HP, lives, first question) once the question
  // pool is ready, and flash the first phase intro — mirrors the Dart
  // source's initState.
  useEffect(() => {
    if (loadState !== 'ready' || !questions.length) return undefined;
    setHp(questions.length);
    setLives(3);
    setCorrectCount(0);
    setIndex(0);
    setPicked(null);
    setRevealed(false);
    setDone(false);
    setWon(false);
    rewardedRef.current = false;
    setShowIntro(true);
    maybeSpeak(questions[0]);
    const id = setTimeout(() => setShowIntro(false), 1100);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadState, questions]);

  useEffect(() => {
    doneRef.current = done;
  }, [done]);

  // Stop any in-flight speech when leaving the page.
  useEffect(() => () => {
    if (typeof window !== 'undefined' && window.speechSynthesis) window.speechSynthesis.cancel();
  }, []);

  const current = questions[index];
  const stars = won && lives === 3 ? 3 : won ? 2 : 1;
  const xpEarned = correctCount * 4 + (won ? 20 : 0);

  useEffect(() => {
    if (!done || rewardedRef.current) return;
    rewardedRef.current = true;
    StreakService.recordActivity();
    XpService.addXp(xpEarned);
    if (won) SoundEffectService.playCelebration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  function finish(didWin) {
    setDone(true);
    setWon(didWin);
  }

  function pick(option) {
    if (!current || revealed || doneRef.current) return;
    const correct = option === current.correctOption;
    const newHp = correct ? hp - 1 : hp;
    const newLives = correct ? lives : lives - 1;

    setPicked(option);
    setRevealed(true);
    setHp(newHp);
    setLives(newLives);

    if (correct) {
      setCorrectCount((c) => c + 1);
      setHitKey((k) => k + 1);
      SoundEffectService.playCorrect();
      if (navigator.vibrate) navigator.vibrate(15);
      LearningProgressService.record(langPair, 'vocabulary', 1, { activityId: `boss:${langPair}:${index}` });
    } else {
      SoundEffectService.playWrong();
      if (navigator.vibrate) navigator.vibrate(35);
    }

    setTimeout(() => {
      if (doneRef.current) return;
      if (newHp <= 0) {
        finish(true);
      } else if (newLives <= 0) {
        finish(false);
      } else if (index + 1 >= questions.length) {
        // Ran out of questions without emptying HP or lives — treat as a
        // loss rather than indexing past the end of the question list.
        finish(false);
      } else {
        const nextIndex = index + 1;
        const nextQuestion = questions[nextIndex];
        const wasNewPhase = nextQuestion.phase !== current.phase;
        setIndex(nextIndex);
        setPicked(null);
        setRevealed(false);
        setShowIntro(wasNewPhase);
        maybeSpeak(nextQuestion);
        if (wasNewPhase) {
          setTimeout(() => setShowIntro(false), 1100);
        }
      }
    }, 500);
  }

  function phaseLabel(phase) {
    if (phase === 'vocabulary') return t('games.bossVocabulary', 'Vocabulary');
    if (phase === 'listening') return t('games.bossListening', 'Listening');
    return t('games.bossGrammar', 'Grammar');
  }

  function renderBattle() {
    if (!current) return null;

    if (showIntro) {
      return (
        <div className="flex flex-col items-center justify-center py-24 text-center boss-intro-pop">
          <style>{`
            @keyframes boss-intro-pop-kf {
              0% { transform: scale(0.5); opacity: 0; }
              60% { transform: scale(1.08); opacity: 1; }
              100% { transform: scale(1); opacity: 1; }
            }
            .boss-intro-pop { animation: boss-intro-pop-kf 500ms cubic-bezier(0.34, 1.56, 0.64, 1); }
          `}</style>
          <span className="text-8xl">{PHASE_EMOJI[current.phase]}</span>
          <p className="mt-3 text-xl font-black text-slate-900">
            {t('games.bossVs', 'VS {{phase}}', { phase: phaseLabel(current.phase) })}
          </p>
        </div>
      );
    }

    return (
      <div>
        <div className="flex gap-1.5">
          {[0, 1, 2].map((i) => (
            <span key={i} style={{ color: '#FF4B4B', fontSize: 18 }}>
              {i < lives ? '♥' : '♡'}
            </span>
          ))}
        </div>

        <div className="mt-3 flex justify-center">
          <ShakeWidget trigger={hitKey}>
            <span className="text-6xl">{PHASE_EMOJI[current.phase]}</span>
          </ShakeWidget>
        </div>

        <div className="mt-2">
          <GameProgressBar value={questions.length ? hp / questions.length : 0} color="#DC2626" />
          <p className="mt-1 text-center text-[11px] text-slate-400">
            {t('games.bossHpLabel', 'HP {{hp}}/{{total}}', { hp, total: questions.length })}
          </p>
        </div>

        <div className="mt-6 flex justify-center">
          {current.speakPrompt ? (
            <button
              type="button"
              onClick={() => maybeSpeak(current)}
              className="grid h-20 w-20 place-items-center rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-3xl text-white shadow-lg shadow-indigo-300/40"
            >
              🔊
            </button>
          ) : (
            <p dir={current.promptDir} className="text-center text-2xl font-black text-slate-900">
              {current.prompt}
            </p>
          )}
        </div>

        <div dir={current.optionsDir} className="mt-6 flex flex-col gap-2.5">
          {current.options.map((option) => {
            const isAnswer = option === current.correctOption;
            const isPicked = option === picked;
            return (
              <ShakeWidget key={option} trigger={revealed && isPicked && !isAnswer}>
                <BossOptionTile
                  text={option}
                  revealed={revealed}
                  isAnswer={isAnswer}
                  isPicked={isPicked}
                  onClick={() => pick(option)}
                />
              </ShakeWidget>
            );
          })}
        </div>
      </div>
    );
  }

  function renderResult() {
    return (
      <div className="relative flex flex-col items-center py-10 text-center">
        {won && <Confetti />}
        <span className="text-6xl">{won ? '🏆' : '💀'}</span>
        <h2 className="mt-3 text-2xl font-black text-slate-900">
          {won ? t('games.bossVictory', 'Boss defeated!') : t('games.bossDefeat', 'You were defeated')}
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          {t('games.bossScoreLine', '{{score}} / {{total}} correct', { score: correctCount, total: questions.length })}
        </p>
        <div className="mt-3">
          <StarRow count={stars} />
        </div>
        <div className="mt-3">
          <FloatingXpBadge xp={xpEarned} />
        </div>
        <div className="mt-8 w-full max-w-xs">
          <button
            type="button"
            onClick={() => navigate('/games')}
            className="w-full rounded-2xl border border-slate-200 py-3 text-sm font-bold text-slate-600 hover:bg-slate-50"
          >
            {t('games.done')}
          </button>
        </div>
      </div>
    );
  }

  if (!nativeCode || !learnCode) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-lg px-4 py-4 sm:px-6">
        <div className="mb-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-500 shadow-sm hover:bg-slate-50"
          >
            ← {t('games.title')}
          </button>
          <h1 className="text-sm font-black text-slate-900">{t('games.bossBattle', 'Boss Battle')}</h1>
          <span className="w-16" />
        </div>

        {loadState === 'loading' && <SkeletonGrid />}

        {loadState === 'insufficient' && (
          <div className="flex flex-col items-center py-16 text-center">
            <span className="text-5xl">🚧</span>
            <p className="mt-4 max-w-xs text-sm font-semibold text-slate-500">
              {t('games.bossNotEnoughContent', 'Not enough content for this language pair yet.')}
            </p>
            <button
              type="button"
              onClick={() => navigate('/games')}
              className="mt-6 rounded-2xl border border-slate-200 px-5 py-3 text-sm font-bold text-slate-600 hover:bg-slate-50"
            >
              {t('games.done')}
            </button>
          </div>
        )}

        {loadState === 'ready' && (done ? renderResult() : renderBattle())}
      </div>
    </div>
  );
}
